import { deserializeSuperjson } from '@lib/superjson';
import { MutationDefinition } from '@reduxjs/toolkit/query';

import { api } from '../api';
import { BaseQuery, EndpointBuilderType, EndpointFunctionType } from './types';

type MutationReturnType<Input, Result> = MutationDefinition<
  Input,
  BaseQuery,
  never,
  Result,
  'api'
>;

function createMutation<Input, Result>(
  builder: EndpointBuilderType,
  query: EndpointFunctionType<Input>,
): MutationReturnType<Input, Result> {
  return builder.mutation<Result, Input>({
    query,
    transformResponse: deserializeSuperjson,
  });
}

/**
 * Creates and injects a new mutation endpoint into the api slice.
 *
 * @param name The name of the mutation hook and endpoint.
 * @param mutation The mutation function that will be used to generate the mutation hook and endpoint.
 * @returns A tuple containing the endpoint and the useMutation hook.
 */
export function injectMutation<Input, Result>(
  name: string,
  mutation: EndpointFunctionType<Input>,
) {
  const injectedApi = api.injectEndpoints({
    endpoints: (builder) => ({
      [name]: createMutation<Input, Result>(builder, mutation),
    }),
    overrideExisting: false,
  });

  const endpoint = injectedApi.endpoints[name];
  const useMutation = endpoint.useMutation;

  return [endpoint, useMutation] as const;
}
